import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, DollarSign, FileText, Eye, Download, TrendingUp,
  Search, Filter, Calendar, ChevronLeft, ChevronRight, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Separator } from '../../ui/separator';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { db } from '../../../lib/db';
import { formatCurrency, getPayrollStatusColor, formatPayrollStatus } from '../../../lib/payrollCalculations';
import { formatDate, getMonthName, getCurrentMonth, getCurrentYear } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';

export default function TeamPayroll() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      // Get team members (for demo, get employees from same department)
      const allUsers = await db.users.toArray();
      const team = allUsers.filter(u => 
        u.profile?.department === user.profile?.department && 
        u.id !== user.id &&
        u.role === 'employee'
      );
      setTeamMembers(team);

      // Get payslips for team
      const teamIds = team.map(t => t.id);
      const allPayslips = await db.payslips.toArray();
      const teamPayslips = allPayslips.filter(p => teamIds.includes(p.userId));
      setPayslips(teamPayslips);

      // Get salary structures
      const salaries = await db.salaryStructures.toArray();
      setSalaryStructures(salaries.filter(s => teamIds.includes(s.userId)));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentMonthPayslips = useMemo(() => {
    return payslips.filter(p => 
      p.month === selectedMonth && 
      p.year === selectedYear
    );
  }, [payslips, selectedMonth, selectedYear]);

  const filteredMembers = teamMembers.filter(member =>
    `${member.profile?.firstName} ${member.profile?.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const monthlyTrend = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const monthPayslips = payslips.filter(p => p.month === month && p.year === selectedYear);
      return {
        month: getMonthName(month).substring(0, 3),
        fullMonth: getMonthName(month),
        total: monthPayslips.reduce((sum, p) => sum + (Number(p.netPay) || 0), 0),
        count: monthPayslips.length
      };
    });
  }, [payslips, selectedYear]);

  const totals = useMemo(() => ({
    gross: currentMonthPayslips.reduce((sum, p) => sum + (Number(p.grossPay) || Number(p.grossEarnings) || 0), 0),
    net: currentMonthPayslips.reduce((sum, p) => sum + (Number(p.netPay) || 0), 0),
    deductions: currentMonthPayslips.reduce((sum, p) => sum + (Number(p.totalDeductions) || ((Number(p.totalPreTax) || 0) + (Number(p.totalTaxes) || 0) + (Number(p.totalPostTax) || 0)) || 0), 0)
  }), [currentMonthPayslips]);

  const getEmployeeName = (userId) => {
    const member = teamMembers.find(m => m.id === userId);
    return member ? `${member.profile?.firstName} ${member.profile?.lastName}` : 'Unknown';
  };

  const getEmployeePayslip = (userId) => {
    return currentMonthPayslips.find(p => p.userId === userId);
  };

  const getEmployeeSalary = (userId) => {
    return salaryStructures.find(s => s.userId === userId);
  };

  const viewPayslip = (payslip) => {
    setSelectedPayslip(payslip);
    setShowPayslipModal(true);
  };

  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Payroll</h1>
          <p className="text-gray-500 mt-1">View payroll information for your team members</p>
        </div>
      </div>

      {/* Month Selector */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-center gap-4">
            <Button variant="ghost" size="icon" onClick={goToPreviousMonth}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900">
                {getMonthName(selectedMonth)} {selectedYear}
              </h2>
              <p className="text-sm text-gray-500">{currentMonthPayslips.length} payslips</p>
            </div>
            <Button variant="ghost" size="icon" onClick={goToNextMonth}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Team Members</p>
                <p className="text-2xl font-bold text-gray-900">{teamMembers.length}</p>
              </div>
              <Users className="w-8 h-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Gross</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.gross)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Net</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totals.net)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Payslips Generated</p>
                <p className="text-2xl font-bold text-violet-600">{currentMonthPayslips.length}</p>
              </div>
              <FileText className="w-8 h-8 text-violet-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Monthly Team Payroll Trend</CardTitle>
          <CardDescription>Total net payroll for your team over the year</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${v / 1000}k`} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={(label) => monthlyTrend.find(d => d.month === label)?.fullMonth}
                />
                <Area 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#14b8a6" 
                  fill="#14b8a6" 
                  fillOpacity={0.3}
                  name="Total Net Payroll"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Team Members Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Team Members</CardTitle>
              <CardDescription>Payroll details for your direct reports</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search team members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Employee</TableHead>
                <TableHead>Position</TableHead>
                <TableHead className="text-right">Gross Salary</TableHead>
                <TableHead className="text-right">Net Salary</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No team members found
                  </TableCell>
                </TableRow>
              ) : (
                filteredMembers.map(member => {
                  const payslip = getEmployeePayslip(member.id);
                  const salary = getEmployeeSalary(member.id);
                  
                  return (
                    <TableRow key={member.id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                            {member.profile?.firstName?.[0]}{member.profile?.lastName?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {member.profile?.firstName} {member.profile?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{member.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-gray-700">{member.profile?.position || 'N/A'}</p>
                      </TableCell>
                      <TableCell className="text-right">
                        <p className="font-medium">
                          {payslip ? formatCurrency(payslip.grossPay || payslip.grossEarnings) : '-'}
                        </p>
                      </TableCell>
                      <TableCell className="text-right">
                        <p className="font-bold text-emerald-600">
                          {payslip ? formatCurrency(payslip.netPay) : '-'}
                        </p>
                      </TableCell>
                      <TableCell>
                        {payslip ? (
                          <Badge className={getPayrollStatusColor(payslip.status)}>
                            {formatPayrollStatus(payslip.status)}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-50">
                            No Payslip
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {payslip && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => viewPayslip(payslip)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payslip Modal */}
      <Dialog open={showPayslipModal} onOpenChange={setShowPayslipModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Payslip Details
            </DialogTitle>
            <DialogDescription>
              {selectedPayslip && `${getMonthName(selectedPayslip.month)} ${selectedPayslip.year}`}
            </DialogDescription>
          </DialogHeader>

          {selectedPayslip && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-14 h-14 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white text-lg font-medium">
                  {getEmployeeName(selectedPayslip.userId).split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900">{getEmployeeName(selectedPayslip.userId)}</p>
                  <p className="text-sm text-gray-500">
                    {teamMembers.find(m => m.id === selectedPayslip.userId)?.profile?.position}
                  </p>
                  {selectedPayslip.workState && (
                    <p className="text-xs text-gray-400">Work State: {selectedPayslip.workState}</p>
                  )}
                </div>
                <div className="ml-auto">
                  <Badge className={getPayrollStatusColor(selectedPayslip.status)}>
                    {formatPayrollStatus(selectedPayslip.status)}
                  </Badge>
                </div>
              </div>

              {/* Earnings */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Earnings</h3>
                <div className="space-y-2 p-4 bg-emerald-50 rounded-lg">
                  {selectedPayslip.earnings?.regular > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Regular Pay</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.regular)}</span>
                    </div>
                  )}
                  {selectedPayslip.earnings?.overtime > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Overtime</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.overtime)}</span>
                    </div>
                  )}
                  {selectedPayslip.earnings?.bonus > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Bonus</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.bonus)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-semibold">
                    <span>Gross Pay</span>
                    <span className="text-emerald-600">{formatCurrency(selectedPayslip.grossPay || selectedPayslip.grossEarnings)}</span>
                  </div>
                </div>
              </div>

              {/* Pre-Tax Deductions */}
              {selectedPayslip.preTaxDeductions && Object.values(selectedPayslip.preTaxDeductions).some(v => v > 0) && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Pre-Tax Deductions</h3>
                  <div className="space-y-2 p-4 bg-violet-50 rounded-lg">
                    {selectedPayslip.preTaxDeductions?.retirement401k > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">401(k)</span>
                        <span className="font-medium text-violet-600">-{formatCurrency(selectedPayslip.preTaxDeductions.retirement401k)}</span>
                      </div>
                    )}
                    {selectedPayslip.preTaxDeductions?.healthInsurance > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Health Insurance</span>
                        <span className="font-medium text-violet-600">-{formatCurrency(selectedPayslip.preTaxDeductions.healthInsurance)}</span>
                      </div>
                    )}
                    {selectedPayslip.preTaxDeductions?.hsa > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">HSA</span>
                        <span className="font-medium text-violet-600">-{formatCurrency(selectedPayslip.preTaxDeductions.hsa)}</span>
                      </div>
                    )}
                    <Separator />
                    <div className="flex justify-between font-semibold">
                      <span>Total Pre-Tax</span>
                      <span className="text-violet-600">-{formatCurrency(selectedPayslip.totalPreTax)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Taxes (US Style) */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Taxes</h3>
                <div className="space-y-2 p-4 bg-red-50 rounded-lg">
                  {selectedPayslip.taxes?.federalIncome > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Federal Income Tax</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.federalIncome)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.socialSecurity > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Social Security</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.socialSecurity)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.medicare > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Medicare</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.medicare)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.stateIncome > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">State Tax ({selectedPayslip.taxes.stateName || selectedPayslip.workState})</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.stateIncome)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.sdi > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">SDI</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.sdi)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-semibold">
                    <span>Total Taxes</span>
                    <span className="text-red-600">-{formatCurrency(selectedPayslip.totalTaxes)}</span>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="p-4 bg-gradient-to-r from-teal-500 to-emerald-600 rounded-lg text-white">
                <div className="flex justify-between items-center">
                  <span className="text-lg">Net Pay</span>
                  <span className="text-3xl font-bold">{formatCurrency(selectedPayslip.netPay)}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayslipModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
